import React, { useState, useEffect, useCallback } from "react";
import {
  Clock,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Info,
  Save,
  CalendarClock,
  Timer,
  Mail,
  Zap,
  X,
  Link,
  Unlink,
  Power,
  PowerOff,
  History,
  ChevronDown,
  ChevronRight,
  Circle,
  Inbox,
} from "lucide-react";
import {
  getOutlookMonitorSchedule,
  updateOutlookMonitorSchedule,
  OutlookMonitorScheduleStatus,
  toggleOutlookMonitorSchedule,
  OutlookMonitorItem,
  listOutlookMonitors,
  addOutlookMonitor,
  removeOutlookMonitor,
  getOutlookMonitorRunHistory,
  OutlookMonitorRunHistoryItem,
  checkOutlookConnection,
  authenticateOutlook,
  getOutlookMonitorNotificationConfig,
  updateOutlookMonitorNotificationConfig,
  OutlookNotificationConfigStatus,
} from "../../../service/apiService";
import OutlookAuth from "../../Outlook/OutlookAuth";

type IntervalUnit = "minutes" | "hours" | "days";

enum LoadingState {
  IDLE = "idle",
  LOADING = "loading",
  SUCCESS = "success",
  ERROR = "error",
}

enum SaveState {
  IDLE = "idle",
  SAVING = "saving",
  SUCCESS = "success",
  ERROR = "error",
}

/* ────────────────────────────────────────────
   Hooks
   ──────────────────────────────────────────── */

const useOutlookMonitorSchedule = () => {
  const [schedule, setSchedule] = useState<OutlookMonitorScheduleStatus | null>(null);
  const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.LOADING);
  const [error, setError] = useState<string | null>(null);

  const fetchSchedule = useCallback(async () => {
    try {
      setLoadingState(LoadingState.LOADING);
      setError(null);
      const data = await getOutlookMonitorSchedule();
      setSchedule(data);
      setLoadingState(LoadingState.SUCCESS);
    } catch (err: any) {
      const errorMessage =
        err?.response?.status === 404
          ? "Outlook schedule not found. Create the EventBridge schedule first."
          : "Failed to load outlook schedule configuration";
      setError(errorMessage);
      setLoadingState(LoadingState.ERROR);
    }
  }, []);

  return {
    schedule,
    setSchedule,
    loadingState,
    error,
    fetchSchedule,
    isLoading: loadingState === LoadingState.LOADING,
  };
};

const useOutlookMonitors = () => {
  const [outlookMonitors, setOutlookMonitors] = useState<OutlookMonitorItem[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.LOADING);
  const [error, setError] = useState<string | null>(null);

  const fetchOutlookMonitors = useCallback(async () => {
    try {
      setLoadingState(LoadingState.LOADING);
      setError(null);
      const data = await listOutlookMonitors();
      setOutlookMonitors(data.emails);
      setLoadingState(LoadingState.SUCCESS);
    } catch (err: any) {
      setError("Failed to load monitored emails");
      setLoadingState(LoadingState.ERROR);
    }
  }, []);

  return {
    outlookMonitors,
    setOutlookMonitors,
    loadingState,
    error,
    fetchOutlookMonitors,
    isLoading: loadingState === LoadingState.LOADING,
  };
};

const useOutlookRunHistory = () => {
  const [runs, setRuns] = useState<OutlookMonitorRunHistoryItem[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.IDLE);
  const [error, setError] = useState<string | null>(null);

  const fetchRunHistory = useCallback(async () => {
    try {
      setLoadingState(LoadingState.LOADING);
      setError(null);
      const data = await getOutlookMonitorRunHistory();
      setRuns(data.runs);
      setLoadingState(LoadingState.SUCCESS);
    } catch (err: any) {
      setError("Failed to load outlook run history");
      setLoadingState(LoadingState.ERROR);
    }
  }, []);

  return {
    runs,
    loadingState,
    error,
    fetchRunHistory,
    isLoading: loadingState === LoadingState.LOADING,
  };
};

const useOutlookNotificationConfig = () => {
  const [notifConfig, setNotifConfig] = useState<OutlookNotificationConfigStatus | null>(null);
  const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.LOADING);
  const [error, setError] = useState<string | null>(null);

  const fetchNotifConfig = useCallback(async () => {
    try {
      setLoadingState(LoadingState.LOADING);
      setError(null);
      const data = await getOutlookMonitorNotificationConfig();
      setNotifConfig(data);
      setLoadingState(LoadingState.SUCCESS);
    } catch (err: any) {
      const errorMessage =
        err?.response?.status === 404
          ? "Notification config not found"
          : "Failed to load notification configuration";
      setError(errorMessage);
      setLoadingState(LoadingState.ERROR);
    }
  }, []);

  return {
    notifConfig,
    setNotifConfig,
    loadingState,
    error,
    fetchNotifConfig,
    isLoading: loadingState === LoadingState.LOADING,
  };
};

/* ────────────────────────────────────────────
   Shared UI Pieces
   ──────────────────────────────────────────── */

const SectionLoadingSkeleton: React.FC<{ color?: string }> = ({
  color = "blue",
}) => (
  <div className="animate-pulse px-5 py-4">
    <div className="flex items-center gap-4 mb-4">
      <div className={`w-10 h-10 bg-${color}-100 rounded-full`} />
      <div className="space-y-2 flex-1">
        <div className="h-4 bg-gray-200 rounded w-48" />
        <div className="h-3 bg-gray-100 rounded w-72" />
      </div>
    </div>
    <div className="space-y-3">
      <div className="h-10 bg-gray-100 rounded-xl" />
      <div className="h-10 bg-gray-100 rounded-xl" />
    </div>
  </div>
);

interface ErrorCardProps {
  message: string;
  onRetry: () => void;
}

const ErrorCard: React.FC<ErrorCardProps> = ({ message, onRetry }) => (
  <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-100 rounded-xl">
    <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
    <div className="flex-1">
      <p className="text-sm text-red-700 mb-2">{message}</p>
      <button
        onClick={onRetry}
        className="inline-flex items-center text-xs font-medium text-red-600 hover:text-red-700"
      >
        <RefreshCw className="h-3 w-3 mr-1" />
        Retry
      </button>
    </div>
  </div>
);

const StatusPill: React.FC<{ isEnabled: boolean }> = ({ isEnabled }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
      isEnabled
        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
        : "bg-gray-50 text-gray-500 border border-gray-200"
    }`}
  >
    <span
      className={`w-1.5 h-1.5 rounded-full ${
        isEnabled ? "bg-emerald-500 animate-pulse" : "bg-gray-400"
      }`}
    />
    {isEnabled ? "Active" : "Inactive"}
  </span>
);

/* ────────────────────────────────────────────
   Step Component
   ──────────────────────────────────────────── */

type StepStatus = "complete" | "current" | "incomplete" | "error";

interface StepProps {
  stepNumber: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  status: StepStatus;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  accentColor: string;
}

const Step: React.FC<StepProps> = ({
  stepNumber,
  title,
  description,
  status,
  isOpen,
  onToggle,
  children,
  accentColor,
}) => {
  return (
    <div
      className={`bg-white rounded-2xl shadow-sm border overflow-hidden transition-all duration-200 ${
        isOpen ? `border-${accentColor}-200 shadow-md` : "border-gray-100"
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-gray-50/50 transition-colors"
      >
        <div
          className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${
            status === "complete"
              ? "bg-emerald-100 text-emerald-700"
              : isOpen
              ? `bg-violet-100 text-violet-700`
              : "bg-gray-100 text-gray-500"
          }`}
        >
          {status === "complete" ? (
            <CheckCircle2 className="h-4.5 w-4.5" />
          ) : (
            stepNumber
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3
              className={`text-sm font-semibold ${
                isOpen ? "text-gray-900" : "text-gray-700"
              }`}
            >
              {title}
            </h3>
            {status === "complete" && !isOpen && (
              <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full font-medium">
                Done
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5 truncate">{description}</p>
        </div>

        {isOpen ? (
          <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
        )}
      </button>

      {isOpen && (
        <div className="px-5 pb-5 pt-1 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

/* ────────────────────────────────────────────
   Main Component
   ──────────────────────────────────────────── */

const OutlookMonitorSchedule: React.FC = () => {
  // ── Schedule hook ──
  const {
    schedule,
    setSchedule,
    loadingState: scheduleLoadingState,
    error: scheduleError,
    fetchSchedule,
    isLoading: isScheduleLoading,
  } = useOutlookMonitorSchedule();

  const [intervalValue, setIntervalValue] = useState<number>(1);
  const [intervalUnit, setIntervalUnit] = useState<IntervalUnit>("days");
  const [saveState, setSaveState] = useState<SaveState>(SaveState.IDLE);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // ── Outlook monitors hook ──
  const {
    outlookMonitors,
    setOutlookMonitors,
    loadingState: outlookMonitorsLoadingState,
    error: outlookMonitorsError,
    fetchOutlookMonitors,
    isLoading: isOutlookMonitorsLoading,
  } = useOutlookMonitors();

  // ── Run history hook ──
  const {
    runs,
    loadingState: runHistoryLoadingState,
    error: runHistoryError,
    fetchRunHistory,
    isLoading: isRunHistoryLoading,
  } = useOutlookRunHistory();

  // ── Notification config hook ──
  const {
    notifConfig,
    setNotifConfig,
    loadingState: notifLoadingState,
    error: notifError,
    fetchNotifConfig,
    isLoading: isNotifLoading,
  } = useOutlookNotificationConfig();

  const [notifRecipientEmail, setNotifRecipientEmail] = useState<string>("");
  const [notifSaveState, setNotifSaveState] = useState<SaveState>(SaveState.IDLE);
  const [notifSaveError, setNotifSaveError] = useState<string | null>(null);
  const [hasNotifChanges, setHasNotifChanges] = useState(false);

  // ── Outlook connection ──
  const [isOutlookConnected, setIsOutlookConnected] = useState(false);
  const [checkingOutlook, setCheckingOutlook] = useState(true);
  const [showOutlookAuth, setShowOutlookAuth] = useState(false);

  // ── Add email form ──
  const [newEmailAddress, setNewEmailAddress] = useState("");
  const [emailSaveState, setEmailSaveState] = useState<SaveState>(SaveState.IDLE);
  const [emailSaveError, setEmailSaveError] = useState<string | null>(null);

  // ── Toggle monitor ──
  const [toggleLoading, setToggleLoading] = useState(false);

  // ── Wizard step management ──
  const [openStep, setOpenStep] = useState<number>(1);

  /* ── Effects ── */

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  useEffect(() => {
    if (schedule) {
      // Outlook is always a once-per-day digest — coerce to daily regardless of
      // what's stored (older schedules may carry a sub-daily rate).
      setIntervalValue(1);
      setIntervalUnit("days");
    }
  }, [schedule]);

  useEffect(() => {
    if (!schedule) return;
    setHasChanges(
      intervalValue !== schedule.interval_value ||
        intervalUnit !== schedule.interval_unit
    );
  }, [intervalValue, intervalUnit, schedule]);

  useEffect(() => {
    if (saveState === SaveState.SUCCESS || saveState === SaveState.ERROR) {
      const t = setTimeout(() => setSaveState(SaveState.IDLE), 3000);
      return () => clearTimeout(t);
    }
  }, [saveState]);

  useEffect(() => {
    fetchOutlookMonitors();
  }, [fetchOutlookMonitors]);

  useEffect(() => {
    fetchNotifConfig();
  }, [fetchNotifConfig]);

  useEffect(() => {
    if (notifConfig) {
      setNotifRecipientEmail(notifConfig.ses_recipient_email);
    }
  }, [notifConfig]);

  useEffect(() => {
    if (!notifConfig) return;
    setHasNotifChanges(
      notifRecipientEmail !== notifConfig.ses_recipient_email
    );
  }, [notifRecipientEmail, notifConfig]);

  useEffect(() => {
    if (notifSaveState === SaveState.SUCCESS || notifSaveState === SaveState.ERROR) {
      const t = setTimeout(() => setNotifSaveState(SaveState.IDLE), 3000);
      return () => clearTimeout(t);
    }
  }, [notifSaveState]);

  useEffect(() => {
    if (emailSaveState === SaveState.SUCCESS || emailSaveState === SaveState.ERROR) {
      const t = setTimeout(() => setEmailSaveState(SaveState.IDLE), 3000);
      return () => clearTimeout(t);
    }
  }, [emailSaveState]);

  useEffect(() => {
    const checkOutlook = async () => {
      setCheckingOutlook(true);
      const connected = await checkOutlookConnection();
      setIsOutlookConnected(connected);
      setCheckingOutlook(false);
    };
    checkOutlook();
  }, []);

  /* ── Handlers ── */

  const handleNotifSave = useCallback(async () => {
    if (!hasNotifChanges || notifSaveState === SaveState.SAVING) return;
    try {
      setNotifSaveState(SaveState.SAVING);
      setNotifSaveError(null);
      const payload: { ses_sender_email?: string; ses_recipient_email?: string } = {};
      if (notifRecipientEmail !== notifConfig?.ses_recipient_email)
        payload.ses_recipient_email = notifRecipientEmail;
      const updated = await updateOutlookMonitorNotificationConfig(payload);
      setNotifConfig((prev) =>
        prev
          ? {
              ...prev,
              ses_recipient_email: updated.ses_recipient_email,
              updated_at: updated.updated_at,
            }
          : prev
      );
      setNotifSaveState(SaveState.SUCCESS);
      setHasNotifChanges(false);
    } catch (err: any) {
      setNotifSaveError(
        err?.response?.data?.detail ||
          "Failed to update notification config. Please try again."
      );
      setNotifSaveState(SaveState.ERROR);
    }
  }, [hasNotifChanges, notifSaveState, notifRecipientEmail, notifConfig, setNotifConfig]);

  const handleAddEmail = useCallback(async () => {
    if (!newEmailAddress.trim() || emailSaveState === SaveState.SAVING) return;
    try {
      setEmailSaveState(SaveState.SAVING);
      setEmailSaveError(null);
      const result = await addOutlookMonitor(newEmailAddress.trim());
      setOutlookMonitors((prev) => [
        ...prev,
        {
          email_address: result.email_address,
          connected_user_id: result.connected_user_id,
          updated_at: result.updated_at,
        },
      ]);
      setNewEmailAddress("");
      setEmailSaveState(SaveState.SUCCESS);
    } catch (err: any) {
      setEmailSaveError(
        err?.response?.data?.detail || "Failed to add email. Please try again."
      );
      setEmailSaveState(SaveState.ERROR);
    }
  }, [newEmailAddress, emailSaveState, setOutlookMonitors]);

  const handleRemoveEmail = useCallback(
    async (emailAddress: string) => {
      try {
        setEmailSaveState(SaveState.SAVING);
        setEmailSaveError(null);
        await removeOutlookMonitor(emailAddress);
        setOutlookMonitors((prev) =>
          prev.filter((e) => e.email_address !== emailAddress)
        );
        setEmailSaveState(SaveState.SUCCESS);
      } catch (err: any) {
        setEmailSaveError(
          err?.response?.data?.detail || "Failed to remove email. Please try again."
        );
        setEmailSaveState(SaveState.ERROR);
      }
    },
    [setOutlookMonitors]
  );

  const handleSaveSchedule = useCallback(async () => {
    if (!hasChanges || saveState === SaveState.SAVING) return;
    try {
      setSaveState(SaveState.SAVING);
      setSaveError(null);
      // Outlook is a once-per-day digest — always every 1 day.
      const updated = await updateOutlookMonitorSchedule(1, "days");
      setSchedule((prev) =>
        prev
          ? {
              ...prev,
              interval_value: updated.interval_value,
              interval_unit: updated.interval_unit,
              schedule_expression: updated.schedule_expression,
            }
          : prev
      );
      setSaveState(SaveState.SUCCESS);
      setHasChanges(false);
    } catch (err: any) {
      setSaveError(
        err?.response?.data?.detail ||
          "Failed to update schedule. Please try again."
      );
      setSaveState(SaveState.ERROR);
    }
  }, [hasChanges, saveState, intervalValue, intervalUnit, setSchedule]);

  const handleToggleMonitor = useCallback(async () => {
    if (!schedule || toggleLoading) return;
    try {
      setToggleLoading(true);
      const newEnabled = !schedule.is_enabled;
      const updated = await toggleOutlookMonitorSchedule(newEnabled);
      setSchedule((prev) =>
        prev
          ? {
              ...prev,
              is_enabled: updated.is_enabled,
            }
          : prev
      );
    } catch (err: any) {
      console.error("Failed to toggle outlook monitor:", err);
    } finally {
      setToggleLoading(false);
    }
  }, [schedule, toggleLoading, setSchedule]);

  const handleOutlookAuthSuccess = useCallback(
    async (code: string) => {
      try {
        await authenticateOutlook(code);
        setIsOutlookConnected(true);
      } catch (err) {
        console.error("Outlook auth error:", err);
      }
    },
    []
  );

  const refreshAll = () => {
    fetchSchedule();
    fetchOutlookMonitors();
    fetchNotifConfig();
  };

  /* ── Derived state ── */

  const ratePreview = `rate(${intervalValue} ${
    intervalValue === 1 ? intervalUnit.replace(/s$/, "") : intervalUnit
  })`;

  const outlookStepStatus: StepStatus = isOutlookConnected
    ? "complete"
    : "incomplete";

  const emailsStepStatus: StepStatus =
    outlookMonitors.length > 0 ? "complete" : "incomplete";

  const notifStepStatus: StepStatus =
    notifConfig && notifConfig.ses_recipient_email ? "complete" : "incomplete";

  const scheduleStepStatus: StepStatus =
    schedule ? "complete" : scheduleLoadingState === LoadingState.ERROR ? "error" : "incomplete";

  const monitorStepStatus: StepStatus = schedule
    ? "complete"
    : "incomplete";

  /* ── Format helpers ── */

  const formatDateTime = (dateStr: string | null): string => {
    if (!dateStr) return "-";
    try {
      const d = new Date(dateStr);
      return d.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case "changed":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
            New Emails
          </span>
        );
      case "unchanged":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200">
            <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
            No new emails
          </span>
        );
      case "baseline":
      case "baseline_reset":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
            Baseline
          </span>
        );
      case "error":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
            Error
          </span>
        );
      case "pending":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
            Pending
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-500 border border-gray-200">
            {status || "Unknown"}
          </span>
        );
    }
  };

  /* ════════════════════════════════════════════
     RENDER
     ════════════════════════════════════════════ */

  return (
    <div className="min-h-screen bg-gradient-to-br p-6">
      <div className="max-w-3xl mx-auto space-y-4">
        {/* ── Page Header ── */}
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
              Outlook Inbox Monitor
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Set up Outlook inbox monitoring in a few steps
            </p>
          </div>
          <div className="flex items-center gap-2">
            {schedule && <StatusPill isEnabled={schedule.is_enabled} />}
            <button
              onClick={refreshAll}
              disabled={isScheduleLoading || isOutlookMonitorsLoading}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all duration-200 shadow-sm disabled:opacity-50"
            >
              <RefreshCw
                className={`h-4 w-4 ${
                  isScheduleLoading || isOutlookMonitorsLoading ? "animate-spin" : ""
                }`}
              />
            </button>
          </div>
        </div>

        {/* ═══════════════════════════════════
           STEP 1 – Connect Outlook
           ═══════════════════════════════════ */}
        <Step
          stepNumber={1}
          title="Connect Outlook"
          description={
            isOutlookConnected
              ? "Your Outlook account is connected"
              : "Authorize Kyva to access your Outlook inbox"
          }
          icon={<Link className="h-5 w-5" />}
          status={outlookStepStatus}
          isOpen={openStep === 1}
          onToggle={() => setOpenStep(openStep === 1 ? 0 : 1)}
          accentColor="violet"
        >
          {checkingOutlook ? (
            <div className="flex items-center gap-3 py-4">
              <RefreshCw className="h-4 w-4 text-violet-500 animate-spin" />
              <span className="text-sm text-gray-500">
                Verifying connection...
              </span>
            </div>
          ) : isOutlookConnected ? (
            <div className="space-y-4 pt-3">
              <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-emerald-800">
                    Outlook Connected
                  </p>
                  <p className="text-xs text-emerald-600 mt-0.5">
                    Your account is linked and ready for inbox monitoring
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowOutlookAuth(true)}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
                >
                  <Unlink className="h-4 w-4 mr-2" />
                  Manage Connection
                </button>
                <button
                  onClick={() => setOpenStep(2)}
                  className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-violet-600 rounded-xl hover:bg-violet-700 transition-all"
                >
                  Next Step
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 pt-3">
              <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    Connection Required
                  </p>
                  <p className="text-xs text-amber-600 mt-0.5">
                    Connect your Outlook to monitor your inbox for new emails.
                    Kyva needs access to read incoming messages.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowOutlookAuth(true)}
                className="inline-flex items-center px-5 py-2.5 text-sm font-semibold text-white bg-violet-600 rounded-xl hover:bg-violet-700 transition-all shadow-sm"
              >
                <Link className="h-4 w-4 mr-2" />
                Connect Outlook
              </button>
            </div>
          )}
        </Step>

        {/* ═══════════════════════════════════
           STEP 2 – Add Email to Monitor
           ═══════════════════════════════════ */}
        <Step
          stepNumber={2}
          title="Add Email Address to Monitor"
          description={
            outlookMonitors.length > 0
              ? `${outlookMonitors.length} email${outlookMonitors.length !== 1 ? "s" : ""} being monitored`
              : "Enter the email address to watch for new messages"
          }
          icon={<Inbox className="h-5 w-5" />}
          status={emailsStepStatus}
          isOpen={openStep === 2}
          onToggle={() => setOpenStep(openStep === 2 ? 0 : 2)}
          accentColor="emerald"
        >
          {isOutlookMonitorsLoading ? (
            <SectionLoadingSkeleton color="emerald" />
          ) : outlookMonitorsLoadingState === LoadingState.ERROR ? (
            <ErrorCard
              message={outlookMonitorsError ?? "Unexpected error"}
              onRetry={fetchOutlookMonitors}
            />
          ) : (
            <div className="space-y-4 pt-3">
              {/* Email List */}
              {outlookMonitors.length > 0 && (
                <div className="space-y-2">
                  {outlookMonitors.map((email) => (
                    <div
                      key={email.email_address}
                      className="rounded-xl p-3 bg-emerald-50 border border-emerald-200"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 min-w-0">
                          <Mail className="h-4 w-4 flex-shrink-0 text-emerald-600" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-emerald-900 truncate">
                              {email.email_address}
                            </p>
                            {email.updated_at && (
                              <p className="text-xs text-emerald-600">
                                Added: {formatDateTime(email.updated_at)}
                              </p>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveEmail(email.email_address)}
                          disabled={emailSaveState === SaveState.SAVING}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                          title="Remove monitored email"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {outlookMonitors.length === 0 && (
                <div className="bg-gray-50 border border-gray-200 border-dashed rounded-xl p-6 text-center">
                  <Mail className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">
                    No email addresses added. Enter your email address to start monitoring.
                  </p>
                </div>
              )}

              {/* Add Email Form */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Email Address
                </label>
                <div className="flex items-center gap-3">
                  <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <Mail className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      value={newEmailAddress}
                      onChange={(e) => setNewEmailAddress(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleAddEmail();
                      }}
                      placeholder="yourname@outlook.com"
                      className="block w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent focus:bg-white transition-all duration-200 placeholder-gray-400"
                    />
                  </div>
                  <button
                    onClick={handleAddEmail}
                    disabled={
                      !isOutlookConnected ||
                      !newEmailAddress.trim() ||
                      emailSaveState === SaveState.SAVING
                    }
                    className={`inline-flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl transition-all shadow-sm ${
                      !isOutlookConnected || !newEmailAddress.trim()
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed shadow-none"
                        : emailSaveState === SaveState.SAVING
                        ? "bg-emerald-400 text-white cursor-not-allowed"
                        : "bg-emerald-600 text-white hover:bg-emerald-700"
                    }`}
                  >
                    {emailSaveState === SaveState.SAVING ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <Mail className="h-4 w-4 mr-2" />
                        Add Email
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Save feedback */}
              {emailSaveState === SaveState.ERROR && emailSaveError && (
                <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <span>{emailSaveError}</span>
                </div>
              )}

              {emailSaveState === SaveState.SUCCESS && (
                <div className="flex items-start gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span>Email list updated successfully!</span>
                </div>
              )}

              {!isOutlookConnected && (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-full">
                  <AlertCircle className="h-3 w-3" />
                  Connect Outlook first (Step 1)
                </span>
              )}

              {isOutlookConnected && outlookMonitors.length > 0 && (
                <button
                  onClick={() => setOpenStep(3)}
                  className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-violet-600 rounded-xl hover:bg-violet-700 transition-all"
                >
                  Next Step
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              )}
            </div>
          )}
        </Step>

        {/* ═══════════════════════════════════
           STEP 3 – Set Notification Email
           ═══════════════════════════════════ */}
        <Step
          stepNumber={3}
          title="Set Notification Email"
          description={
            notifConfig?.ses_recipient_email
              ? `Notifications sent to ${notifConfig.ses_recipient_email}`
              : "Configure who receives inbox change alerts"
          }
          icon={<Mail className="h-5 w-5" />}
          status={notifStepStatus}
          isOpen={openStep === 3}
          onToggle={() => setOpenStep(openStep === 3 ? 0 : 3)}
          accentColor="purple"
        >
          {isNotifLoading ? (
            <SectionLoadingSkeleton color="purple" />
          ) : notifLoadingState === LoadingState.ERROR ? (
            <ErrorCard
              message={notifError ?? "Unexpected error"}
              onRetry={fetchNotifConfig}
            />
          ) : (
            <div className="space-y-4 pt-3">
              <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700 leading-relaxed">
                  Recipient email addresses must be{" "}
                  <strong>verified in AWS SES</strong> before use. Unverified
                  emails will be rejected.
                </p>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-gray-700">
                  Recipient Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <Mail className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    value={notifRecipientEmail}
                    onChange={(e) => setNotifRecipientEmail(e.target.value)}
                    placeholder="recipient@example.com"
                    className="block w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent focus:bg-white transition-all duration-200 placeholder-gray-400"
                  />
                </div>
                <p className="text-xs text-gray-400">
                  Receives alerts when new emails are detected in the monitored inbox
                </p>
              </div>

              {notifSaveState === SaveState.ERROR && notifSaveError && (
                <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <span>{notifSaveError}</span>
                </div>
              )}

              <div className="flex items-center gap-3">
                <button
                  onClick={handleNotifSave}
                  disabled={!hasNotifChanges || notifSaveState === SaveState.SAVING}
                  className={`inline-flex items-center px-4 py-2 text-sm font-semibold rounded-xl transition-all shadow-sm ${
                    notifSaveState === SaveState.SAVING
                      ? "bg-violet-400 text-white cursor-not-allowed"
                      : notifSaveState === SaveState.SUCCESS
                      ? "bg-emerald-500 text-white"
                      : !hasNotifChanges
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed shadow-none"
                      : "bg-violet-600 text-white hover:bg-violet-700"
                  }`}
                >
                  {notifSaveState === SaveState.SAVING ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : notifSaveState === SaveState.SUCCESS ? (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Saved!
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Email
                    </>
                  )}
                </button>

                {hasNotifChanges && notifSaveState === SaveState.IDLE && (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                    Unsaved changes
                  </span>
                )}

                {notifConfig?.ses_recipient_email && !hasNotifChanges && (
                  <button
                    onClick={() => setOpenStep(4)}
                    className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-violet-600 rounded-xl hover:bg-violet-700 transition-all"
                  >
                    Next Step
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </button>
                )}
              </div>
            </div>
          )}
        </Step>

        {/* ═══════════════════════════════════
           STEP 4 – Set Schedule
           ═══════════════════════════════════ */}
        <Step
          stepNumber={4}
          title="Set Monitor Schedule"
          description={
            schedule
              ? `Checking every ${schedule.interval_value} ${schedule.interval_unit}`
              : "Define how often the inbox is checked for new emails"
          }
          icon={<CalendarClock className="h-5 w-5" />}
          status={scheduleStepStatus}
          isOpen={openStep === 4}
          onToggle={() => setOpenStep(openStep === 4 ? 0 : 4)}
          accentColor="blue"
        >
          {isScheduleLoading ? (
            <SectionLoadingSkeleton color="blue" />
          ) : scheduleLoadingState === LoadingState.ERROR ? (
            <ErrorCard
              message={scheduleError ?? "Unexpected error"}
              onRetry={fetchSchedule}
            />
          ) : (
            <div className="space-y-4 pt-3">
              {schedule && (
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Clock className="h-3.5 w-3.5 text-blue-500" />
                      <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">
                        Interval
                      </span>
                    </div>
                    <p className="text-lg font-bold text-blue-900">
                      {schedule.interval_value}
                      <span className="text-xs font-medium text-blue-600 ml-1">
                        {schedule.interval_unit}
                      </span>
                    </p>
                  </div>

                  <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Timer className="h-3.5 w-3.5 text-indigo-500" />
                      <span className="text-xs font-medium text-indigo-600 uppercase tracking-wide">
                        Expression
                      </span>
                    </div>
                    <p className="text-xs font-bold text-indigo-900 font-mono truncate">
                      {schedule.schedule_expression}
                    </p>
                  </div>

                  <div className="bg-slate-50 border border-slate-100 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Zap className="h-3.5 w-3.5 text-slate-500" />
                      <span className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                        Timezone
                      </span>
                    </div>
                    <p className="text-xs font-bold text-slate-900">
                      {schedule.timezone}
                    </p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Update Check Frequency
                </label>
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <label className="block text-xs text-gray-500 mb-1">Every</label>
                    <input
                      type="number"
                      value={1}
                      disabled
                      title="Outlook sends one digest per day"
                      className="w-24 px-3 py-2 border border-gray-200 rounded-xl text-sm font-semibold text-gray-900 bg-gray-100 cursor-not-allowed"
                    />
                  </div>
                  <div className="flex-shrink-0">
                    <label className="block text-xs text-gray-500 mb-1">Unit</label>
                    <select
                      value={intervalUnit}
                      onChange={(e) => setIntervalUnit(e.target.value as IntervalUnit)}
                      className="w-32 px-3 py-2 border border-gray-200 rounded-xl text-sm font-semibold text-gray-900 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all cursor-pointer"
                    >
                      <option value="days">Days</option>
                    </select>
                  </div>
                  <div className="flex-1 pt-4">
                    <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl">
                      <Info className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                      <code className="text-xs font-mono text-gray-600">{ratePreview}</code>
                    </div>
                  </div>
                </div>
              </div>

              {saveState === SaveState.ERROR && saveError && (
                <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <span>{saveError}</span>
                </div>
              )}

              <div className="flex items-center gap-3">
                <button
                  onClick={handleSaveSchedule}
                  disabled={!hasChanges || saveState === SaveState.SAVING}
                  className={`inline-flex items-center px-4 py-2 text-sm font-semibold rounded-xl transition-all shadow-sm ${
                    saveState === SaveState.SAVING
                      ? "bg-blue-400 text-white cursor-not-allowed"
                      : saveState === SaveState.SUCCESS
                      ? "bg-emerald-500 text-white"
                      : !hasChanges
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed shadow-none"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  {saveState === SaveState.SAVING ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : saveState === SaveState.SUCCESS ? (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Saved!
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Schedule
                    </>
                  )}
                </button>

                {hasChanges && saveState === SaveState.IDLE && (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                    Unsaved changes
                  </span>
                )}

                {schedule && !hasChanges && (
                  <button
                    onClick={() => setOpenStep(5)}
                    className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-violet-600 rounded-xl hover:bg-violet-700 transition-all"
                  >
                    Next Step
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </button>
                )}
              </div>
            </div>
          )}
        </Step>

        {/* ═══════════════════════════════════
           STEP 5 – Enable / Disable Monitor
           ═══════════════════════════════════ */}
        <Step
          stepNumber={5}
          title="Enable or Disable Monitor"
          description={
            schedule?.is_enabled
              ? "Monitor is currently active and checking for new emails"
              : "Monitor is currently paused"
          }
          icon={
            schedule?.is_enabled ? (
              <Power className="h-5 w-5" />
            ) : (
              <PowerOff className="h-5 w-5" />
            )
          }
          status={monitorStepStatus}
          isOpen={openStep === 5}
          onToggle={() => setOpenStep(openStep === 5 ? 0 : 5)}
          accentColor="emerald"
        >
          {isScheduleLoading ? (
            <SectionLoadingSkeleton color="emerald" />
          ) : !schedule ? (
            <div className="py-4 text-sm text-gray-500">
              Schedule not loaded. Please configure the schedule first.
            </div>
          ) : (
            <div className="space-y-4 pt-3">
              <div
                className={`rounded-xl p-5 border ${
                  schedule.is_enabled
                    ? "bg-emerald-50 border-emerald-200"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div
                      className={`p-3 rounded-xl ${
                        schedule.is_enabled ? "bg-emerald-100" : "bg-gray-200"
                      }`}
                    >
                      {schedule.is_enabled ? (
                        <Power className="h-6 w-6 text-emerald-600" />
                      ) : (
                        <PowerOff className="h-6 w-6 text-gray-500" />
                      )}
                    </div>
                    <div>
                      <p className="text-base font-semibold text-gray-900">
                        Outlook Monitor
                      </p>
                      <p className="text-sm text-gray-500">
                        {schedule.is_enabled
                          ? `Active - checking every ${schedule.interval_value} ${schedule.interval_unit}`
                          : "Paused - inbox is not being checked"}
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={handleToggleMonitor}
                    disabled={toggleLoading}
                    className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                      schedule.is_enabled
                        ? "bg-emerald-500 focus:ring-emerald-400"
                        : "bg-gray-300 focus:ring-gray-400"
                    } ${toggleLoading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                    role="switch"
                    aria-checked={schedule.is_enabled}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ${
                        schedule.is_enabled ? "translate-x-6" : "translate-x-1"
                      }`}
                    >
                      {toggleLoading && (
                        <RefreshCw className="h-3 w-3 text-gray-400 animate-spin m-1" />
                      )}
                    </span>
                  </button>
                </div>
              </div>

              {schedule.is_enabled && (
                <div className="bg-white border border-gray-100 rounded-xl p-4">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                    Monitor Summary
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">Emails:</span>{" "}
                      <span className="font-medium text-gray-900">
                        {outlookMonitors.length}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Interval:</span>{" "}
                      <span className="font-medium text-gray-900">
                        {schedule.interval_value} {schedule.interval_unit}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Status:</span>{" "}
                      <StatusPill isEnabled={schedule.is_enabled} />
                    </div>
                  </div>
                </div>
              )}

              {schedule && (
                <button
                  onClick={() => {
                    setOpenStep(6);
                    fetchRunHistory();
                  }}
                  className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-violet-600 rounded-xl hover:bg-violet-700 transition-all"
                >
                  View Run History
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              )}
            </div>
          )}
        </Step>

        {/* ═══════════════════════════════════
           STEP 6 – Run History
           ═══════════════════════════════════ */}
        <Step
          stepNumber={6}
          title="Run History"
          description="View recent inbox monitoring runs and their results"
          icon={<History className="h-5 w-5" />}
          status={runs.length > 0 ? "complete" : "incomplete"}
          isOpen={openStep === 6}
          onToggle={() => {
            setOpenStep(openStep === 6 ? 0 : 6);
            if (openStep !== 6) fetchRunHistory();
          }}
          accentColor="slate"
        >
          <div className="space-y-4 pt-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">Recent inbox check results</p>
              <button
                onClick={fetchRunHistory}
                disabled={isRunHistoryLoading}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 transition-colors"
              >
                <RefreshCw
                  className={`h-3.5 w-3.5 ${isRunHistoryLoading ? "animate-spin" : ""}`}
                />
                Refresh
              </button>
            </div>

            {isRunHistoryLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-5 w-5 text-gray-400 animate-spin mr-2" />
                <span className="text-sm text-gray-500">Loading run history...</span>
              </div>
            ) : runHistoryLoadingState === LoadingState.ERROR ? (
              <ErrorCard
                message={runHistoryError ?? "Failed to load run history"}
                onRetry={fetchRunHistory}
              />
            ) : runs.length === 0 ? (
              <div className="bg-gray-50 border border-gray-200 border-dashed rounded-xl p-8 text-center">
                <History className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">
                  No run history available yet.
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  History will appear after the outlook monitor runs for the first time.
                </p>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Email
                      </th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Last Checked
                      </th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {runs.map((run, index) => (
                      <tr
                        key={`${run.email_address}-${index}`}
                        className="hover:bg-gray-50/50 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <span className="font-medium text-gray-900 truncate max-w-[200px]">
                              {run.email_address}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {formatDateTime(run.last_checked)}
                        </td>
                        <td className="px-4 py-3">
                          {getStatusBadge(run.status)}
                          {run.new_emails_count > 0 && (
                            <p className="text-xs text-amber-600 mt-1">
                              {run.new_emails_count} new email(s)
                            </p>
                          )}
                          {run.error_message && (
                            <p className="text-xs text-red-500 mt-1 truncate max-w-[200px]">
                              {run.error_message}
                            </p>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Step>
      </div>

      {/* ── Modals ── */}
      <OutlookAuth
        isOpen={showOutlookAuth}
        onClose={() => {
          setShowOutlookAuth(false);
          checkOutlookConnection().then(setIsOutlookConnected);
        }}
        onAuthSuccess={handleOutlookAuthSuccess}
      />
    </div>
  );
};

export default OutlookMonitorSchedule;
